import GlobalConst from "../constants/GlobalConst";
import {deepClone} from "../utils/AppUtils";
import ContestPick from "./ContestPick";
import {ContestPickResult, ContestPickType, ParlayStatus, ParlayType, PayoutModel} from "../constants/PickXEnums";
import ExactParlayPayoutCalculator from "../services/ExactParlayPayoutCalculator";
import FlexParlayPayoutCalculator from "../services/FlexParlayPayoutCalculator";
import Pick12PayoutCalculator from "../services/Pick12ParlayPayoutCalculator";
import Pick16PayoutCalculator from "../services/Pick16ParlayPayoutCalculator";
import _ from "lodash";
import PrizeAssetsPayoutCalculator from "../services/PrizeAssetsPayoutCalculator";

export default class Parlay {

    Id;
    AccountId
    Wager
    WagerCurrency
    Payout
    PayoutModel
    PayoutCurrency
    ContestsPicks={}
    DateCreated
    DateStarted
    DateCompleted
    DateCancelled
    Status=1
    CancellationReason
    ClosingRollingPrizeAmount
    RollingPrizeAmount;
    ParlayNum;

    parlayType;
    preSelectedParlayId;

    constructor(data) {
        Object.assign(this, deepClone(data));

        for(const contestId in this.ContestsPicks)
            this.ContestsPicks[contestId] = new ContestPick(this.ContestsPicks[contestId])

        if(!this.parlayType)
            this.parlayType = ParlayType.UserParlay;
        if(!this.ContestsPicks)
            this.ContestsPicks = {};
    }

    setContestPick(contestId, pick){
        const hasPick = this.hasPick(contestId);
        const isSamePick = this.isContestPickSelected(contestId, pick);
        const hasReachedLimit = this.isMaximumContestsReached;
        const isCreateYourOwn = this.parlayType === ParlayType.CreateYourOwn;

        if (!hasPick && !hasReachedLimit && isCreateYourOwn) {
            this.ContestsPicks[contestId] = new ContestPick({ ContestId: contestId, Pick: pick });
        } else if (hasPick && !isSamePick) {
            this.ContestsPicks[contestId] = new ContestPick({ ContestId: contestId, Pick: pick });
        } else if (isSamePick && isCreateYourOwn) {
            delete this.ContestsPicks[contestId];
        }
    }
    setEmptyContestsPicks( contestIds ){
        contestIds.forEach(contestId=>{
            this.ContestsPicks[contestId] = new ContestPick({ ContestId: contestId, Pick: null });
        });
        return this;
    }

    hasPick(contestId){
        return !!this.ContestsPicks[contestId];
    }
    isContestPickSelected(contestId, pick){
        return this.hasPick(contestId) &&
            this.ContestsPicks[contestId]?.Pick === pick &&
            this.ContestsPicks[contestId]?.Pick !== null;
    }
    get totalContests(){
        return Object.entries(this.ContestsPicks).length
    }
    get hasAtLeastOneContest(){
        return this.totalContests > 0;
    }
    get canAddMoreContests(){
        return this.totalContests < GlobalConst.CUSTOM_PARLAY.MAX_PLAYERS;
    }
    get hasMinContestsRequired(){
        return this.totalContests >= GlobalConst.CUSTOM_PARLAY.MIN_PLAYERS;
    }
    get isMaximumContestsReached(){
        return this.totalContests === GlobalConst.CUSTOM_PARLAY.MAX_PLAYERS;
    }
    get contestIds(){
        return Object.entries(this.ContestsPicks).map(([contestId])=>contestId);
    }

    get hasSelectedAllContestsPicks(){
        return this.contestIds.every(contestId=> !!this.ContestsPicks[contestId]?.Pick);
    }

    get isWon(){
        return this.Status === ParlayStatus.Completed && this.Payout > 0;
    }

    get isLost(){
        return this.Status === ParlayStatus.Completed && this.Payout === 0;
    }

    get hasAnyWonOrLost(){
        return Object.values(this.ContestsPicks).some(pick=> pick.Result === ContestPickResult.Won || pick.Result === ContestPickResult.Lost)
    }
    get winningOdds(){
        let P = 0.0;

        if(this.Status === ParlayStatus.Cancelled)
            return 0.0;

        const picks = Object.values(this.ContestsPicks);
        const totalWon = picks.filter(pick => pick.Result === ContestPickResult.Won).length;
        const totalLost = picks.filter(pick => pick.Result === ContestPickResult.Lost).length;
        const n = picks.length - totalWon - totalLost;

        if( totalWon === picks.length)
            P = 1.0;
        else if( totalLost === picks.length)
            P = 0.0;
        else
            switch (this.PayoutModel) {
                case PayoutModel.Pick12:
                    P = Pick12PayoutCalculator.getWinningProbability(totalWon, totalLost);
                    break;
                case PayoutModel.Pick16:
                    P = Pick16PayoutCalculator.getWinningProbability(totalWon, totalLost);
                    break;
                case PayoutModel.Exact:
                    P = ExactParlayPayoutCalculator.getWinningProbability(totalWon, totalLost, n);
                    break;
                case PayoutModel.Flex:
                    P = FlexParlayPayoutCalculator.getWinningProbability(totalWon, totalLost, n);
                    break;
            }

        let formatted = parseFloat((P*100).toFixed(2));
        return formatted % 1 === 0 ? formatted.toFixed(1) : formatted.toString();
    }

    get canSubmit(){
        if(this.Wager <= 0)
            return false;
        if(this.parlayType === ParlayType.CreateYourOwn && this.hasMinContestsRequired)
            return true;
        if(this.parlayType === ParlayType.CreateFromPreSelected && this.hasSelectedAllContestsPicks)
            return true;
        return false;
    }


    getPayouts(contests, prizeAsset) {
        if(!contests || contests.length === 0)
            return [];
        let rollingPriceAmount = this.RollingPrizeAmount;
        if (this.Status === ParlayStatus.Cancelled || this.Status === ParlayStatus.Completed) {
            rollingPriceAmount = this.ClosingRollingPrizeAmount || 0;
        }

        const results = [];
        switch (this.PayoutModel) {
            case PayoutModel.Exact:
                results.push(...new ExactParlayPayoutCalculator().computePayouts(this, contests));
                break;
            case PayoutModel.Flex:
                results.push(...new FlexParlayPayoutCalculator().computePayouts(this, contests));
                break;
            case PayoutModel.Pick12:
                results.push(...new Pick12PayoutCalculator().calculateDistribution(this.Wager, rollingPriceAmount));
                break;
            case PayoutModel.Pick16:
                results.push(...new Pick16PayoutCalculator().calculateDistribution(this.Wager, rollingPriceAmount));
                break;
            case PayoutModel.Assets:
                results.push(...new PrizeAssetsPayoutCalculator().calculateDistribution(this.Wager, prizeAsset))
                break;
        }
        return results;
    }

    get payoutModelStr(){
        switch(this.PayoutModel){
            case PayoutModel.Flex:
                return "FLEX";
            case PayoutModel.Exact:
                return "EXACT";
            case PayoutModel.Pick12:
                return "PICK12";
            case PayoutModel.Pick16:
                return "PICK16";
        }
        return "";
    }

    toDto(){
        return deepClone(this);
    }

    get isStarted(){
        return Object.values(this.ContestsPicks)
                     .some(pick=> pick.Result !== ContestPickResult.Pending)
    }

    // 2 Players Per Team Max Rule
    canAdd(contestId, contests, players, games) {
        const isAlreadySelected = this.contestIds.includes(contestId);
        if (isAlreadySelected)
            return true;
        if (![PayoutModel.Exact, PayoutModel.Flex].includes(this.PayoutModel))
            return true;

        const teamCounts = {};
        for (const player of players) {
            let teamId = player.TeamId;

            const playerContest = contests.find(c => c.PlayerId === player.PlayerId);
            if (playerContest.SportId === 3){
               const teamIds = teamId.split(",");
                //only grab the valid teamId from the games
               teamId = teamIds.find(id => games.some(g => g.HomeTeamId === id || g.AwayTeamId === id));
            }


            teamCounts[teamId] = (teamCounts[teamId] || 0) + 1;
            if (teamCounts[teamId] > 2) return false;
        }
        return true;
    }

}